import moment from 'moment';

/**
 * Calculate regulatory deadlines based on incident occurrence time and organizational rules
 * @param {Date|Timestamp} occurredAt - When the incident occurred
 * @param {Array} regulatoryRules - Organization's regulatory rules from onboarding
 * @param {string} incidentType - Type of incident (crisis type)
 * @param {Array} selectedTriggers - User-confirmed regulatory triggers (optional)
 * @returns {Array} Array of regulatory deadline objects
 */
export const calculateRegulatoryDeadlines = (occurredAt, regulatoryRules, incidentType, selectedTriggers = null) => {
  if (!occurredAt || !regulatoryRules || !Array.isArray(regulatoryRules)) {
    return [];
  }

  const incidentMoment = moment(occurredAt.toDate ? occurredAt.toDate() : occurredAt);
  const deadlines = [];

  // Use selected triggers if provided, otherwise fall back to auto-matching
  let matchingRules;
  if (selectedTriggers && Array.isArray(selectedTriggers) && selectedTriggers.length > 0) {
    // Use user-confirmed triggers
    matchingRules = regulatoryRules.filter(rule => 
      selectedTriggers.includes(rule.trigger)
    );
  } else {
    // Fall back to auto-matching for backward compatibility
    matchingRules = regulatoryRules.filter(rule => 
      isIncidentTypeMatch(incidentType, rule.trigger)
    );
  }

  matchingRules.forEach((rule, index) => {
    const deadlineValue = parseInt(rule.deadlineValue) || 0;
    const deadlineUnit = rule.deadlineUnit || 'hours';
    
    // Handle immediate notifications
    let deadlineMoment;
    let urgencyLevel = 'normal';
    let urgencyColor = '#1890ff'; // Blue
    let hoursRemaining;
    
    if (rule.immediateNotificationRequired || deadlineUnit === 'immediate') {
      // For immediate notifications, deadline is at the same time as incident occurrence
      deadlineMoment = incidentMoment.clone();
      urgencyLevel = 'immediate';
      urgencyColor = '#ff0000'; // Bright red
      hoursRemaining = 0; // Always immediate
    } else {
      // Calculate deadline time for regular notifications
      deadlineMoment = incidentMoment.clone().add(deadlineValue, deadlineUnit);
      
      // Determine urgency level based on time remaining
      const now = moment();
      const timeRemaining = deadlineMoment.diff(now);
      hoursRemaining = moment.duration(timeRemaining).asHours();
      
      if (hoursRemaining <= 0) {
        urgencyLevel = 'overdue';
        urgencyColor = '#f5222d'; // Red
      } else if (hoursRemaining <= 2) {
        urgencyLevel = 'critical';
        urgencyColor = '#fa541c'; // Orange-red
      } else if (hoursRemaining <= 6) {
        urgencyLevel = 'urgent';
        urgencyColor = '#faad14'; // Orange
      } else if (hoursRemaining <= 24) {
        urgencyLevel = 'warning';
        urgencyColor = '#fadb14'; // Yellow
      }
    }

    const isImmediate = rule.immediateNotificationRequired || deadlineUnit === 'immediate';
    
    deadlines.push({
      id: `regulatory-${index}`,
      type: 'regulatory_deadline',
      trigger: rule.trigger,
      regulator: rule.regulator,
      method: Array.isArray(rule.method) ? rule.method : [rule.method],
      sender: rule.sender,
      deadlineTime: deadlineMoment.toDate(),
      urgencyLevel,
      urgencyColor,
      timeRemaining: hoursRemaining,
      deadlineValue,
      deadlineUnit,
      isOverdue: hoursRemaining <= 0 && !isImmediate,
      isImmediate,
      title: `${rule.regulator} Notification${isImmediate ? ' (IMMEDIATE)' : ''}`,
      description: `${rule.trigger} notification due to ${rule.regulator}${isImmediate ? ' - IMMEDIATE ACTION REQUIRED' : ''}`,
      content: `⚖️ ${rule.regulator}${isImmediate ? ' - IMMEDIATE' : ''}`,
      className: `regulatory-deadline regulatory-${urgencyLevel}${isImmediate ? ' immediate-notification' : ''}`
    });
  });

  return deadlines.sort((a, b) => moment(a.deadlineTime).diff(moment(b.deadlineTime)));
};

/**
 * Check if incident type matches a regulatory trigger
 * @param {string} incidentType - The incident type from the incident
 * @param {string} trigger - The trigger from regulatory rule
 * @returns {boolean} Whether they match
 */
export const isIncidentTypeMatch = (incidentType, trigger) => {
  if (!incidentType || !trigger) return false;
  
  const normalizedIncident = incidentType.toLowerCase();
  const normalizedTrigger = trigger.toLowerCase();
  
  // Direct match
  if (normalizedIncident === normalizedTrigger) return true;
  
  // Keyword matching for common incident types
  const incidentKeywords = normalizedIncident.split(/[\s\-_\/]+/);
  const triggerKeywords = normalizedTrigger.split(/[\s\-_\/]+/);
  
  // Check for keyword overlaps
  const keywordMatches = incidentKeywords.some(incidentWord => 
    triggerKeywords.some(triggerWord => 
      incidentWord.includes(triggerWord) || triggerWord.includes(incidentWord)
    )
  );
  
  if (keywordMatches) return true;
  
  // Specific incident type mappings
  const incidentMappings = {
    'cyber incident': ['cyber', 'breach', 'data breach', 'security incident', 'hacking', 'malware'],
    'fire': ['fire', 'building fire', 'workplace fire', 'emergency evacuation'],
    'medical emergency': ['medical', 'injury', 'fatality', 'workplace incident', 'serious injury'],
    'critical incident': ['critical', 'major incident', 'emergency'],
    'active threat': ['threat', 'violence', 'security threat', 'workplace violence'],
    'flood': ['flood', 'water damage', 'natural disaster'],
    'earthquake': ['earthquake', 'seismic', 'natural disaster'],
    'cyclone / severe weather': ['cyclone', 'storm', 'severe weather', 'natural disaster'],
    'suspicious item / bomb threat': ['bomb', 'suspicious', 'threat', 'evacuation'],
    'protest / civil unrest': ['protest', 'unrest', 'demonstration', 'civil'],
    'reputational incident': ['reputation', 'media', 'public relations', 'brand'],
    'extortion': ['extortion', 'blackmail', 'ransom'],
    'kidnap for ransom (kre)': ['kidnap', 'ransom', 'kre', 'abduction'],
    'meeting/event incident': ['event', 'meeting', 'conference', 'gathering'],
    'aggressive customer': ['customer', 'aggressive', 'violence', 'threat']
  };
  
  // Check if incident type maps to trigger keywords
  const mappedKeywords = incidentMappings[normalizedIncident] || [];
  const triggerMatchesMapped = mappedKeywords.some(keyword => 
    normalizedTrigger.includes(keyword)
  );
  
  if (triggerMatchesMapped) return true;
  
  // Check reverse mapping (trigger type maps to incident keywords)
  const triggerMappedKeywords = incidentMappings[normalizedTrigger] || [];
  const incidentMatchesMapped = triggerMappedKeywords.some(keyword => 
    normalizedIncident.includes(keyword)
  );
  
  return incidentMatchesMapped;
};

/**
 * Create incident occurrence marker for timeline
 * @param {Date|Timestamp} occurredAt - When the incident occurred
 * @param {string} incidentTitle - Title of the incident
 * @returns {Object} Timeline item for incident occurrence
 */
export const createIncidentOccurrenceMarker = (occurredAt, incidentTitle) => {
  if (!occurredAt) return null;
  
  const occurrenceTime = occurredAt.toDate ? occurredAt.toDate() : occurredAt;
  
  // Create minimalist icon-only content for incident occurrence
  const incidentContent = `
    <div class="c3-timeline-item">
      <span class="timeline-icon">⚠️</span>
      <span class="c3-timeline-label">${incidentTitle}</span>
    </div>
  `;
  
  return {
    id: 'incident-occurrence',
    content: incidentContent,
    start: occurrenceTime,
    type: 'point',
    className: 'incident-occurrence-marker',
    title: `INCIDENT OCCURRENCE\n${incidentTitle}\nTime: ${moment(occurrenceTime).format('DD/MM/YYYY HH:mm')}`,
    group: 'Incident Events',
    editable: true
  };
};

/**
 * Format time remaining for display
 * @param {number} hoursRemaining - Hours remaining until deadline
 * @returns {string} Formatted time remaining
 */
export const formatTimeRemaining = (hoursRemaining) => {
  if (hoursRemaining <= 0) {
    const hoursOverdue = Math.abs(hoursRemaining);
    if (hoursOverdue < 1) {
      const minutesOverdue = Math.round(hoursOverdue * 60);
      return `${minutesOverdue} minutes overdue`;
    }
    return `${Math.round(hoursOverdue)} hours overdue`;
  }
  
  if (hoursRemaining < 1) {
    const minutesRemaining = Math.round(hoursRemaining * 60);
    return `${minutesRemaining} minutes remaining`;
  }
  
  if (hoursRemaining < 24) {
    return `${Math.round(hoursRemaining)} hours remaining`;
  }
  
  const daysRemaining = Math.round(hoursRemaining / 24);
  return `${daysRemaining} days remaining`;
};

/**
 * Get urgency icon based on urgency level
 * @param {string} urgencyLevel - The urgency level
 * @returns {string} Icon for the urgency level
 */
export const getUrgencyIcon = (urgencyLevel) => {
  switch (urgencyLevel) {
    case 'overdue':
      return '🚨';
    case 'critical':
      return '⚠️';
    case 'urgent':
      return '⏰';
    case 'warning':
      return '⚡';
    default:
      return '📋';
  }
};

/**
 * Create regulatory deadline records in Firestore for an incident
 * @param {string} incidentId - The incident ID
 * @param {string} orgId - The organization ID
 * @param {Date} occurredAt - When the incident occurred
 * @param {Array} regulatoryRules - The organization's regulatory rules
 * @param {string} crisisType - Type of crisis
 * @param {Array} selectedTriggers - User-confirmed regulatory triggers
 * @param {string} userId - User creating the records
 * @param {Object} db - Firestore database instance
 */
export const createRegulatoryDeadlineRecords = async (
  incidentId,
  orgId, 
  occurredAt,
  regulatoryRules,
  crisisType,
  selectedTriggers,
  userId,
  db
) => {
  const { collection, addDoc, serverTimestamp, Timestamp } = await import('firebase/firestore');
  
  try {
  
    
    // Calculate deadlines using existing logic
    const calculatedDeadlines = calculateRegulatoryDeadlines(
      occurredAt,
      regulatoryRules,
      crisisType,
      selectedTriggers
    );
    
    // Create Firestore records for each deadline
    const regulatoryDeadlinesRef = collection(db, 'c3_regulatory_deadlines');
    const createPromises = calculatedDeadlines.map(deadline => {
      const recordData = {
        incidentId,
        orgId,
        regulator: deadline.regulator,
        trigger: deadline.trigger,
        deadlineTime: Timestamp.fromDate(deadline.deadlineTime),
        method: deadline.method,
        sender: deadline.sender,
        urgencyLevel: deadline.urgencyLevel,
        urgencyColor: deadline.urgencyColor,
        className: deadline.className,
        status: 'pending',
        completedAt: null,
        completedBy: null,
        evidenceUrl: null,
        notificationsSent: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        deleted: false
      };
      
      return addDoc(regulatoryDeadlinesRef, recordData);
    });
    
    const results = await Promise.all(createPromises);

    return results;
    
  } catch (error) {
    console.error('❌ Error creating regulatory deadline records:', error);
    throw error;
  }
};

/**
 * Update all regulatory deadline times when incident occurrence changes
 * @param {string} incidentId - The incident ID
 * @param {string} orgId - The organization ID
 * @param {Date} newOccurredAt - New incident occurrence time
 * @param {Array} regulatoryRules - The organization's regulatory rules
 * @param {string} userId - User making the update
 * @param {Object} db - Firestore database instance
 */
export const updateRegulatoryDeadlineTimes = async (
  incidentId,
  orgId,
  newOccurredAt,
  regulatoryRules,
  userId,
  db
) => {
  const { collection, query, where, getDocs, updateDoc, serverTimestamp, Timestamp } = await import('firebase/firestore');
  
  try {
    console.log('🔄 Updating regulatory deadline times for incident:', incidentId);
    // Ensure newOccurredAt is a proper Date object
    let occurredAtDate;
    if (newOccurredAt && typeof newOccurredAt.toDate === 'function') {
      // Firebase Timestamp
      occurredAtDate = newOccurredAt.toDate();
    } else if (newOccurredAt instanceof Date) {
      // Already a Date object
      occurredAtDate = newOccurredAt;
    } else if (typeof newOccurredAt === 'string' || typeof newOccurredAt === 'number') {
      // String or timestamp
      occurredAtDate = new Date(newOccurredAt);
    } else {
      throw new Error('Invalid newOccurredAt format - must be Date, Timestamp, string, or number');
    }
    
    console.log('🔍 Input parameters:', {
      incidentId,
      orgId,
      newOccurredAt: occurredAtDate.toISOString(),
      regulatoryRulesCount: regulatoryRules.length,
      userId
    });
    
    // Fetch existing regulatory deadline records with orgId filter
    const regulatoryDeadlinesRef = collection(db, 'c3_regulatory_deadlines');
    const regulatoryQuery = query(
      regulatoryDeadlinesRef,
      where('incidentId', '==', incidentId),
      where('orgId', '==', orgId),
      where('deleted', '==', false)
    );
    
    const snapshot = await getDocs(regulatoryQuery);
    
    // Update each deadline record
    const updatePromises = snapshot.docs.map(doc => {
      const deadline = doc.data();
      
      // Find the matching regulatory rule
      const matchingRule = regulatoryRules.find(rule => 
        rule.regulator === deadline.regulator && 
        rule.trigger === deadline.trigger
      );
      
      if (!matchingRule) {
        console.warn('⚠️ No matching rule found for deadline:', deadline);
        return Promise.resolve();
      }
      
      // Calculate new deadline time using same logic as calculateRegulatoryDeadlines
      const deadlineValue = parseInt(matchingRule.deadlineValue) || 0;
      const deadlineUnit = matchingRule.deadlineUnit || 'hours';
      
      let newDeadlineTime;
      if (matchingRule.immediateNotificationRequired || deadlineUnit === 'immediate') {
        // For immediate notifications, deadline is at the same time as incident occurrence
        newDeadlineTime = new Date(occurredAtDate.getTime());
      } else {
        // Calculate deadline time for regular notifications using moment
        const incidentMoment = moment(occurredAtDate);
        const deadlineMoment = incidentMoment.clone().add(deadlineValue, deadlineUnit);
        newDeadlineTime = deadlineMoment.toDate();
      }
      
      // Validate the calculated time
      if (!newDeadlineTime || isNaN(newDeadlineTime.getTime())) {
        console.error('❌ Invalid deadline time calculated for rule:', matchingRule);
        return Promise.resolve();
      }
      
      console.log('✅ Updating deadline:', {
        regulator: deadline.regulator,
        trigger: deadline.trigger,
        oldTime: deadline.deadlineTime?.toDate(),
        newTime: newDeadlineTime,
        rule: { deadlineValue, deadlineUnit, immediate: matchingRule.immediateNotificationRequired }
      });
      
      return updateDoc(doc.ref, {
        deadlineTime: Timestamp.fromDate(newDeadlineTime),
        updatedAt: serverTimestamp(),
        updatedBy: userId
      });
    });
    
    await Promise.all(updatePromises);
    console.log('✅ Updated', updatePromises.length, 'regulatory deadline times');
    
  } catch (error) {
    console.error('❌ Error updating regulatory deadline times:', error);
    throw error;
  }
};

/**
 * Delete regulatory deadline records for an incident
 * @param {string} incidentId - The incident ID
 * @param {string} userId - User deleting the records
 * @param {Object} db - Firestore database instance
 */
export const deleteRegulatoryDeadlineRecords = async (incidentId, userId, db) => {
  const { collection, query, where, getDocs, updateDoc, serverTimestamp } = await import('firebase/firestore');
  
  try {
    console.log('🗑️ Deleting regulatory deadline records for incident:', incidentId);
    
    const regulatoryDeadlinesRef = collection(db, 'c3_regulatory_deadlines');
    const regulatoryQuery = query(
      regulatoryDeadlinesRef,
      where('incidentId', '==', incidentId),
      where('deleted', '==', false)
    );
    
    const snapshot = await getDocs(regulatoryQuery);
    
    // Soft delete all records
    const deletePromises = snapshot.docs.map(doc => 
      updateDoc(doc.ref, {
        deleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: userId
      })
    );
    
    await Promise.all(deletePromises);
    console.log('✅ Deleted', deletePromises.length, 'regulatory deadline records');
    
  } catch (error) {
    console.error('❌ Error deleting regulatory deadline records:', error);
    throw error;
  }
};

/**
 * Update regulatory deadline status (pending, notified, completed, overdue)
 * @param {string} deadlineId - The deadline record ID
 * @param {string} status - New status
 * @param {string} userId - User making the update
 * @param {Object} db - Firestore database instance
 * @param {Object} metadata - Additional metadata (evidenceUrl, notes, etc.)
 */
export const updateRegulatoryDeadlineStatus = async (deadlineId, status, userId, db, metadata = {}) => {
  const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
  
  try {
    const deadlineRef = doc(db, 'c3_regulatory_deadlines', deadlineId);
    const updateData = {
      status,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
      ...metadata
    };
    
    if (status === 'completed') {
      updateData.completedAt = serverTimestamp();
      updateData.completedBy = userId;
    }
    
    await updateDoc(deadlineRef, updateData);

    
  } catch (error) {
    console.error('❌ Error updating regulatory deadline status:', error);
    throw error;
  }
};

/**
 * Universal regulatory deadlines manager - works for any crisis, CMT, organization
 * Comprehensive CRUD operations with full error handling and validation
 */

/**
 * Get all regulatory deadlines for an incident with full filtering and sorting
 * @param {string} incidentId - The incident ID
 * @param {string} orgId - The organization ID
 * @param {Object} db - Firestore database instance
 * @param {Object} options - Query options (includeDeleted, sortBy, filterBy)
 * @returns {Promise<Array>} Array of regulatory deadline records
 */
export const getRegulatoryDeadlines = async (incidentId, orgId, db, options = {}) => {
  const { collection, query, where, orderBy, getDocs } = await import('firebase/firestore');
  
  try {
  
    
    const regulatoryDeadlinesRef = collection(db, 'c3_regulatory_deadlines');
    let regulatoryQuery = query(
      regulatoryDeadlinesRef,
      where('incidentId', '==', incidentId),
      where('orgId', '==', orgId)
    );
    
    // Include deleted records if requested
    if (!options.includeDeleted) {
      regulatoryQuery = query(regulatoryQuery, where('deleted', '==', false));
    }
    
    // Add sorting
    const sortField = options.sortBy || 'deadlineTime';
    const sortDirection = options.sortDirection || 'asc';
    regulatoryQuery = query(regulatoryQuery, orderBy(sortField, sortDirection));
    
    const snapshot = await getDocs(regulatoryQuery);
    const deadlines = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      deadlineTime: doc.data().deadlineTime?.toDate(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
      completedAt: doc.data().completedAt?.toDate(),
      deletedAt: doc.data().deletedAt?.toDate()
    }));
    
    // Apply additional filters if specified
    let filteredDeadlines = deadlines;
    if (options.filterBy) {
      const { field, value, operator = '==' } = options.filterBy;
      filteredDeadlines = deadlines.filter(deadline => {
        const fieldValue = deadline[field];
        switch (operator) {
          case '==': return fieldValue === value;
          case '!=': return fieldValue !== value;
          case '>': return fieldValue > value;
          case '<': return fieldValue < value;
          case '>=': return fieldValue >= value;
          case '<=': return fieldValue <= value;
          case 'includes': return Array.isArray(fieldValue) ? fieldValue.includes(value) : fieldValue?.includes(value);
          case 'startsWith': return fieldValue?.startsWith(value);
          case 'endsWith': return fieldValue?.endsWith(value);
          default: return fieldValue === value;
        }
      });
    }
    
    console.log('✅ Fetched regulatory deadlines:', {
      total: deadlines.length,
      filtered: filteredDeadlines.length,
      byStatus: {
        pending: filteredDeadlines.filter(d => d.status === 'pending').length,
        notified: filteredDeadlines.filter(d => d.status === 'notified').length,
        completed: filteredDeadlines.filter(d => d.status === 'completed').length,
        overdue: filteredDeadlines.filter(d => d.status === 'overdue').length
      }
    });
    
    return filteredDeadlines;
    
  } catch (error) {
    console.error('❌ Error fetching regulatory deadlines:', error);
    throw error;
  }
};

/**
 * Universal regulatory deadlines synchronization
 * Handles any number of triggers for any organization/crisis type
 * @param {string} incidentId - The incident ID
 * @param {string} orgId - The organization ID
 * @param {Date|Timestamp} occurredAt - When the incident occurred
 * @param {Array} regulatoryRules - Organization's regulatory rules
 * @param {string} crisisType - Type of crisis
 * @param {Array} selectedTriggers - User-selected regulatory triggers
 * @param {string} userId - User performing the sync
 * @param {Object} db - Firestore database instance
 * @returns {Promise<Object>} Sync results with counts and changes
 */
export const syncRegulatoryDeadlines = async (
  incidentId,
  orgId,
  occurredAt,
  regulatoryRules,
  crisisType,
  selectedTriggers,
  userId,
  db
) => {
  const { serverTimestamp } = await import('firebase/firestore');
  
  try {
    // Ensure occurredAt is a proper Date object
    let occurredAtDate;
    if (occurredAt && typeof occurredAt.toDate === 'function') {
      // Firebase Timestamp
      occurredAtDate = occurredAt.toDate();
    } else if (occurredAt instanceof Date) {
      // Already a Date object
      occurredAtDate = occurredAt;
    } else if (typeof occurredAt === 'string' && occurredAt !== 'Not set') {
      // Valid string timestamp (but not display text)
      occurredAtDate = new Date(occurredAt);
      // Check if the date is valid
      if (isNaN(occurredAtDate.getTime())) {
        console.warn('🚨 Invalid date string for occurredAt, using current time:', occurredAt);
        occurredAtDate = new Date();
      }
    } else if (typeof occurredAt === 'number') {
      // Numeric timestamp
      occurredAtDate = new Date(occurredAt);
    } else {
      // Handle invalid or missing occurredAt (including 'Not set', null, undefined)
      console.warn('🚨 Invalid or missing occurredAt, using current time. Received:', occurredAt);
      occurredAtDate = new Date();
    }
    
    console.log('🔄 Starting universal regulatory deadlines sync:', {
      incidentId,
      orgId,
      crisisType,
      selectedTriggersCount: selectedTriggers?.length || 0,
      regulatoryRulesCount: regulatoryRules?.length || 0,
      occurredAt: occurredAtDate.toISOString()
    });
    
    // Validate inputs
    if (!incidentId || !orgId || !occurredAtDate || !Array.isArray(regulatoryRules)) {
      throw new Error('Missing required parameters for regulatory deadlines sync');
    }
    
    // Get existing regulatory deadlines
    const existingDeadlines = await getRegulatoryDeadlines(incidentId, orgId, db);
    const existingTriggers = existingDeadlines.map(d => d.trigger);
    
    // Normalize selected triggers
    const normalizedSelectedTriggers = Array.isArray(selectedTriggers) ? selectedTriggers : [];
    
    console.log('📊 Sync analysis:', {
      existingTriggers,
      selectedTriggers: normalizedSelectedTriggers,
      existingCount: existingDeadlines.length,
      selectedCount: normalizedSelectedTriggers.length
    });
    
    // Find triggers to add and remove
    const triggersToAdd = normalizedSelectedTriggers.filter(trigger => !existingTriggers.includes(trigger));
    const triggersToRemove = existingTriggers.filter(trigger => !normalizedSelectedTriggers.includes(trigger));
    
    console.log('🔍 Sync changes needed:', {
      triggersToAdd,
      triggersToRemove,
      addCount: triggersToAdd.length,
      removeCount: triggersToRemove.length
    });
    
    const results = {
      added: 0,
      removed: 0,
      updated: 0,
      errors: [],
      addedDeadlines: [],
      removedDeadlines: [],
      updatedDeadlines: []
    };
    
    // Add new regulatory deadlines AND notification tasks
    if (triggersToAdd.length > 0) {
      try {
        const rulesToAdd = regulatoryRules.filter(rule => triggersToAdd.includes(rule.trigger));
        console.log('➕ Creating deadlines and tasks for new triggers:', { rulesToAdd: rulesToAdd.length });
        
        // Create regulatory deadline records
        const createdDeadlines = await createRegulatoryDeadlineRecords(
          incidentId,
          orgId,
          occurredAtDate,
          rulesToAdd,
          crisisType,
          triggersToAdd,
          userId,
          db
        );
        
        results.added = createdDeadlines.length;
        results.addedDeadlines = createdDeadlines;
    
        
        // Note: Automatic task creation removed per user request
        // Regulatory deadlines will appear on timeline only
        
      } catch (error) {
        console.error('❌ Error adding regulatory deadlines:', error);
        results.errors.push({ operation: 'add', error: error.message });
      }
    }
    
    // Remove regulatory deadlines AND notification tasks (soft delete)
    if (triggersToRemove.length > 0) {
      try {
        const deadlinesToRemove = existingDeadlines.filter(d => triggersToRemove.includes(d.trigger));
        console.log('➖ Removing deadlines and tasks for removed triggers:', { deadlinesToRemove: deadlinesToRemove.length });
        
        // Remove regulatory deadlines
        const { doc, updateDoc } = await import('firebase/firestore');
        const removePromises = deadlinesToRemove.map(async (deadline) => {
          const deadlineRef = doc(db, 'c3_regulatory_deadlines', deadline.id);
          await updateDoc(deadlineRef, {
            deleted: true,
            deletedAt: serverTimestamp(),
            deletedBy: userId,
            deletionReason: 'trigger_removed'
          });
          return deadline;
        });
        
        const removedDeadlines = await Promise.all(removePromises);
        results.removed = removedDeadlines.length;
        results.removedDeadlines = removedDeadlines;
        console.log(`✅ Removed ${results.removed} regulatory deadlines`);
        
        // Note: Automatic task removal removed per user request
        // Only regulatory deadlines are managed automatically
        
      } catch (error) {
        console.error('❌ Error removing regulatory deadlines:', error);
        results.errors.push({ operation: 'remove', error: error.message });
      }
    }
    
    // Update existing deadlines if incident time changed
    const remainingDeadlines = existingDeadlines.filter(d => normalizedSelectedTriggers.includes(d.trigger));
    if (remainingDeadlines.length > 0) {
      try {
        console.log('🔄 Updating existing deadlines for time changes:', { count: remainingDeadlines.length });
        
        await updateRegulatoryDeadlineTimes(incidentId, orgId, occurredAtDate, regulatoryRules, userId, db);
        results.updated = remainingDeadlines.length;
        results.updatedDeadlines = remainingDeadlines;
        console.log(`✅ Updated ${results.updated} regulatory deadlines`);
        
      } catch (error) {
        console.error('❌ Error updating regulatory deadlines:', error);
        results.errors.push({ operation: 'update', error: error.message });
      }
    }
    
    console.log('🎯 Regulatory deadlines sync completed:', results);
    return results;
    
  } catch (error) {
    console.error('❌ Error in regulatory deadlines sync:', error);
    throw error;
  }
};

/**
 * Bulk update regulatory deadline statuses
 * @param {Array} deadlineIds - Array of deadline IDs to update
 * @param {string} status - New status
 * @param {string} userId - User making the update
 * @param {Object} db - Firestore database instance
 * @param {Object} metadata - Additional metadata
 * @returns {Promise<Array>} Updated deadline records
 */
export const bulkUpdateRegulatoryDeadlineStatus = async (deadlineIds, status, userId, db, metadata = {}) => {
  const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
  
  try {
    console.log('🔄 Bulk updating regulatory deadline statuses:', {
      count: deadlineIds.length,
      status,
      userId
    });
    
    const updatePromises = deadlineIds.map(async (deadlineId) => {
      const deadlineRef = doc(db, 'c3_regulatory_deadlines', deadlineId);
      const updateData = {
        status,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
        ...metadata
      };
      
      // Add completion timestamp if marking as completed
      if (status === 'completed') {
        updateData.completedAt = serverTimestamp();
        updateData.completedBy = userId;
      }
      
      await updateDoc(deadlineRef, updateData);
      return { id: deadlineId, status, ...updateData };
    });
    
    const results = await Promise.all(updatePromises);
    console.log(`✅ Bulk updated ${results.length} regulatory deadline statuses`);
    return results;
    
  } catch (error) {
    console.error('❌ Error bulk updating regulatory deadline statuses:', error);
    throw error;
  }
};

/**
 * Get regulatory deadlines statistics for dashboard/reporting
 * @param {string} incidentId - The incident ID
 * @param {string} orgId - The organization ID
 * @param {Object} db - Firestore database instance
 * @returns {Promise<Object>} Statistics object
 */
export const getRegulatoryDeadlinesStats = async (incidentId, orgId, db) => {
  try {
    const deadlines = await getRegulatoryDeadlines(incidentId, orgId, db);
    const now = new Date();
    
    const stats = {
      total: deadlines.length,
      byStatus: {
        pending: deadlines.filter(d => d.status === 'pending').length,
        notified: deadlines.filter(d => d.status === 'notified').length,
        completed: deadlines.filter(d => d.status === 'completed').length,
        overdue: deadlines.filter(d => d.status === 'overdue').length
      },
      byUrgency: {
        immediate: deadlines.filter(d => d.urgencyLevel === 'immediate').length,
        critical: deadlines.filter(d => d.urgencyLevel === 'critical').length,
        urgent: deadlines.filter(d => d.urgencyLevel === 'urgent').length,
        warning: deadlines.filter(d => d.urgencyLevel === 'warning').length,
        normal: deadlines.filter(d => d.urgencyLevel === 'normal').length
      },
      byRegulator: {},
      upcomingDeadlines: deadlines.filter(d => d.deadlineTime > now && d.status !== 'completed').length,
      overdueDeadlines: deadlines.filter(d => d.deadlineTime <= now && d.status !== 'completed').length,
      completionRate: deadlines.length > 0 ? (deadlines.filter(d => d.status === 'completed').length / deadlines.length * 100).toFixed(1) : 0
    };
    
    // Count by regulator
    deadlines.forEach(deadline => {
      const regulator = deadline.regulator || 'Unknown';
      stats.byRegulator[regulator] = (stats.byRegulator[regulator] || 0) + 1;
    });
    
    console.log('📊 Regulatory deadlines statistics:', stats);
    return stats;
    
  } catch (error) {
    console.error('❌ Error getting regulatory deadlines statistics:', error);
    throw error;
  }
};

// Note: Task creation functions removed per user request
// Only regulatory deadlines are managed automatically

// Placeholder function to prevent import errors
export const createRegulatoryNotificationTasks = async () => {

  return [];
};

// Placeholder function to prevent import errors
export const removeRegulatoryNotificationTasks = async () => {
  console.log('⚠️ Task removal disabled per user request');
  return [];
};

// Placeholder function to prevent import errors
export const ensureRegulatoryTasksExist = async () => {
  console.log('⚠️ Task consistency check disabled per user request');
  return { tasksCreated: 0, errors: [] };
}; 

/**
 * Update all regulatory notification task due dates when incident occurrence changes
 * @param {string} incidentId - The incident ID
 * @param {string} orgId - The organization ID
 * @param {Date} newOccurredAt - New incident occurrence time
 * @param {Array} regulatoryRules - The organization's regulatory rules
 * @param {string} userId - User making the update
 * @param {Object} db - Firestore database instance
 */
export const updateRegulatoryTaskTimes = async (
  incidentId,
  orgId,
  newOccurredAt,
  regulatoryRules,
  userId,
  db
) => {
  const { collection, query, where, getDocs, updateDoc, serverTimestamp, Timestamp } = await import('firebase/firestore');
  
  try {
    console.log('🔄 Updating regulatory notification task times for incident:', incidentId);
    
    // Ensure newOccurredAt is a proper Date object
    let occurredAtDate;
    if (newOccurredAt && typeof newOccurredAt.toDate === 'function') {
      // Firebase Timestamp
      occurredAtDate = newOccurredAt.toDate();
    } else if (newOccurredAt instanceof Date) {
      // Already a Date object
      occurredAtDate = newOccurredAt;
    } else if (typeof newOccurredAt === 'string' || typeof newOccurredAt === 'number') {
      // String or timestamp
      occurredAtDate = new Date(newOccurredAt);
    } else {
      throw new Error('Invalid newOccurredAt format - must be Date, Timestamp, string, or number');
    }
    
    console.log('🔍 Task update parameters:', {
      incidentId,
      orgId,
      newOccurredAt: occurredAtDate.toISOString(),
      regulatoryRulesCount: regulatoryRules.length,
      userId
    });
    
    // Fetch existing regulatory notification tasks with orgId filter
    const tasksRef = collection(db, 'c3_tasks');
    const tasksQuery = query(
      tasksRef,
      where('incidentId', '==', incidentId),
      where('orgId', '==', orgId),
      where('type', '==', 'regulatory_notification')
    );
    
    const snapshot = await getDocs(tasksQuery);
    console.log(`🔍 Found ${snapshot.docs.length} regulatory notification tasks to update`);
    
    // Helper function to get time multiplier
    const getTimeMultiplier = (unit) => {
      switch(unit) {
        case 'minutes': return 60 * 1000;
        case 'hours': return 60 * 60 * 1000;
        case 'days': return 24 * 60 * 60 * 1000;
        case 'weeks': return 7 * 24 * 60 * 60 * 1000;
        case 'immediate': return 0;
        default: return 60 * 60 * 1000; // Default to hours
      }
    };
    
    // Get the doc import once outside the loop
    const { doc: docImport } = await import('firebase/firestore');
    
    // Update each task's due date
    const updatePromises = snapshot.docs.map(async (doc) => {
      const task = doc.data();
      
      // Find the matching regulatory rule
      const matchingRule = regulatoryRules.find(rule => 
        rule.regulator === task.regulator && 
        rule.trigger === task.regulatoryTrigger
      );
      
      if (!matchingRule) {
        console.warn('⚠️ No matching rule found for task:', task);
        return Promise.resolve();
      }
      
      // Calculate new due date using same logic as task creation
      const deadlineValue = parseInt(matchingRule.deadlineValue) || 0;
      const deadlineUnit = matchingRule.deadlineUnit || 'hours';
      
      let newDueDate;
      if (matchingRule.immediateNotificationRequired || deadlineUnit === 'immediate') {
        // For immediate notifications, due date is 15 minutes from now (not from incident time)
        newDueDate = new Date(Date.now() + (15 * 60 * 1000));
      } else {
        // Calculate due date from incident occurrence time
        const timeMultiplier = getTimeMultiplier(deadlineUnit);
        newDueDate = new Date(occurredAtDate.getTime() + (deadlineValue * timeMultiplier));
      }
      
      // Validate the calculated time
      if (!newDueDate || isNaN(newDueDate.getTime())) {
        console.error('❌ Invalid due date calculated for task:', task);
        return Promise.resolve();
      }
      
      console.log('✅ Updating task due date:', {
        taskName: task.name,
        regulator: task.regulator,
        trigger: task.regulatoryTrigger,
        oldDueDate: task.dueDate?.toDate(),
        newDueDate: newDueDate,
        rule: { deadlineValue, deadlineUnit, immediate: matchingRule.immediateNotificationRequired }
      });
      
      // Update the task
      const taskRef = docImport(db, 'c3_tasks', doc.id);
      
      return updateDoc(taskRef, {
        dueDate: Timestamp.fromDate(newDueDate),
        updatedAt: serverTimestamp(),
        updatedBy: userId,
        updateReason: 'incident_occurrence_time_changed'
      });
    });
    
    await Promise.all(updatePromises);

    
    return { updated: updatePromises.length };
    
  } catch (error) {
    console.error('❌ Error updating regulatory notification task times:', error);
    throw error;
  }
}; 